import { getJobs } from "@stryker/core/api/jobs";
import { QueryKeys } from "@stryker/core/queryKeys";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import EmptyState from "@/components/EmptyState";
import DeleteJobModal from "@/components/job/DeleteJobModal";
import DuplicateJobModal from "@/components/job/DuplicateJobModal";
import JobCard from "@/components/job/JobCard";
import NoCreditsModal from "@/components/job/NoCreditsModal";
import { Typography } from "@/components/ui/typography";
import useOrganization from "@/hooks/useOrganization";

type MobileJobsListProps = {
  search: string;
  jobType?: "composite" | "mechanical" | "all";
  onCreateJob: () => void;
};

export default function MobileJobsList({ search, jobType, onCreateJob }: MobileJobsListProps) {
  const loadMoreRef = useRef(null);

  const [showNoCredits, setShowNoCredits] = useState(false);
  const [jobIdToRemove, setJobIdToRemove] = useState<null | number>(null);
  const [jobIdToDuplicate, setJobIdToDuplicate] = useState<null | number>(null);

  const { data, fetchNextPage, hasNextPage, error } = useInfiniteQuery({
    queryKey: [QueryKeys.JobsList, search, jobType ?? "all"],
    queryFn: async ({ pageParam = 1 }) => {
      return await getJobs({
        jobNumber: search,
        page: pageParam,
        jobType: jobType === "all" ? undefined : jobType,
      });
    },
    getNextPageParam: (lastPage) => {
      const current = Number(lastPage.Pagination.currentPage);
      return current === lastPage.Pagination.totalPages ? undefined : current + 1;
    },
    select: (data) => {
      return {
        ...data,
        pages: data.pages.map((response) => ({
          ...response,
          Pagination: {
            ...response.Pagination,
            currentPage: Number.parseInt(response.Pagination.currentPage, 10),
          },
        })),
      };
    },
    initialPageParam: 1,
  });

  const { data: organization } = useOrganization();
  const hasCredits = !!organization?.balanceValue;

  const handleDuplicate = (id: number | null) => {
    if (!id) return;
    if (hasCredits) {
      setJobIdToDuplicate(id);
    } else {
      setShowNoCredits(true);
    }
  };

  useEffect(() => {
    if (!hasNextPage) return; // If there's no next page, don't even set up the observer

    const observer = new IntersectionObserver((entries) => {
      // Check if the entry is intersecting and trigger fetchNextPage
      if (entries[0].isIntersecting) {
        void fetchNextPage();
      }
    });

    // Start observing the ref
    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    // Clean up the observer when the component is unmounted
    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
    };
  }, [hasNextPage, fetchNextPage]);

  // Show error message instead of jobs list when API fails
  if (error) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center py-8">
        <Typography variant="text-lg" weight="semibold" className="mb-2 text-red-600">
          Failed to load jobs
        </Typography>
        <Typography variant="text-small" className="text-gray-600">
          There was an error loading the jobs list. Please try again later.
        </Typography>
      </div>
    );
  }

  return (
    <>
      <div className="mb-11 flex flex-col gap-4">
        {data?.pages
          .flatMap((page) => page.results)
          .map((job) => (
            <JobCard
              job={job}
              jobType={jobType}
              key={job.id}
              setJobIdToDuplicate={handleDuplicate}
              setJobIdToRemove={setJobIdToRemove}
            />
          ))}
        {data?.pages[0]?.Pagination.count === 0 && <EmptyState onSubmit={onCreateJob} />}
        <div ref={loadMoreRef} />
      </div>
      <DeleteJobModal jobIdToRemove={jobIdToRemove} setJobIdToRemove={setJobIdToRemove} />
      <DuplicateJobModal
        jobIdToDuplicate={jobIdToDuplicate}
        setJobIdToDuplicate={setJobIdToDuplicate}
        job={data?.pages.flatMap((page) => page.results).find((job) => job.id === jobIdToDuplicate) || null}
      />
      <NoCreditsModal isOpen={showNoCredits} setIsOpen={setShowNoCredits} />
    </>
  );
}
